import { createMediaMathClient } from "@/apis/clients/mediamathClient";
import {
  chunkArray,
  fetchAllPages,
  MAX_IDS_PER_QUERY,
  MAX_PAGE_LIMIT,
} from "@/apis/clients/pagination";
import {
  getMinSelectableDate,
  toISODate,
} from "@/layouts/AppLayout/dateRangePresets";
import type {
  CampaignSummary,
  CampaignSummaryListResponse,
} from "@/types/campaign";

// A campaign that ended before the earliest selectable report date has nothing
// reportable inside the dashboard's rolling 1-year window, so it is left out
// of every campaign list rather than offered as a dead-end selection.
export const buildCampaignsQuery = (filter: string, now: Date): string =>
  `${filter}&end_date>=${toISODate(getMinSelectableDate(now))}`;

const listCampaignsPage = (q: string, pageOffset: number) =>
  createMediaMathClient()
    .get("campaigns", {
      searchParams: {
        q: buildCampaignsQuery(q, new Date()),
        page_limit: MAX_PAGE_LIMIT,
        page_offset: pageOffset,
        full: true,
        sort_by: "-start_date",
      },
    })
    .json<CampaignSummaryListResponse>();

export const listCampaignsByOrganization = (organizationId: number) =>
  fetchAllPages((pageOffset) =>
    listCampaignsPage(`organization_id==${organizationId}`, pageOffset)
  );

export const listCampaignsByAgencies = async (
  agencyIds: number[]
): Promise<CampaignSummary[]> => {
  const results = await Promise.all(
    chunkArray(agencyIds, MAX_IDS_PER_QUERY).map((chunk) =>
      fetchAllPages((pageOffset) =>
        listCampaignsPage(`agency_id==(${chunk.join(",")})`, pageOffset)
      )
    )
  );
  return results.flat();
};

export const listCampaignsByAdvertisers = async (
  advertiserIds: number[]
): Promise<CampaignSummary[]> => {
  const results = await Promise.all(
    chunkArray(advertiserIds, MAX_IDS_PER_QUERY).map((chunk) =>
      fetchAllPages((pageOffset) =>
        listCampaignsPage(`advertiser_id==(${chunk.join(",")})`, pageOffset)
      )
    )
  );
  return results.flat();
};
